import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconPencil, IconPower } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { Can, EmptyMessage } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";

function statusColor(status) {
  if (status === 1) return "green";
  if (status === 2) return "red";
  return "gray";
}

export default function SelectItemsPanel({
  conceptualRootId,
  conceptualItemId,
  instrumentItemId,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState("");
  const [position, setPosition] = useState(null);
  const [creating, setCreating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchItems();
  }, [conceptualRootId, conceptualItemId, instrumentItemId]);

  function basePath() {
    return `/conceptual-roots/${conceptualRootId}/conceptual-items/${conceptualItemId}/instrument-items/${instrumentItemId}`;
  }

  function fetchItems() {
    setLoading(true);
    axios
      .post(`${basePath()}/retrieve-select-items/`, {
        status: "entire",
        page: 0,
        page_size: 100,
      })
      .then(({ data }) => {
        const rows = data.response[0]?.data || [];
        setItems(rows);
        setDrafts(
          Object.fromEntries(rows.map((row) => [row.id, row.label || ""]))
        );
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onCreate() {
    if (!label.trim()) {
      toast.error(t("validation:required"));
      return;
    }
    setCreating(true);
    const body = { label: label.trim() };
    if (position != null && position !== "") {
      body.position = Number(position);
    }
    axios
      .post(`${basePath()}/select-items/`, body)
      .then(() => {
        setCreating(false);
        setLabel("");
        setPosition(null);
        setShowCreate(false);
        toast.success(t("created"));
        fetchItems();
      })
      .catch((err) => {
        setCreating(false);
        toast.error(err);
      });
  }

  function toggleActive(itemId) {
    setTogglingId(itemId);
    axios
      .post(`${basePath()}/select-items/${itemId}/active-toggle/`)
      .then(() => {
        setTogglingId(null);
        toast.success(t("updated"));
        fetchItems();
      })
      .catch((err) => {
        setTogglingId(null);
        toast.error(err);
      });
  }

  function updateLabel(itemId) {
    const value = (drafts[itemId] || "").trim();
    if (!value) {
      toast.error(t("validation:required"));
      return;
    }
    setSavingId(itemId);
    axios
      .put(`${basePath()}/select-items/${itemId}/`, { label: value })
      .then(() => {
        setSavingId(null);
        setEditingId(null);
        toast.success(t("updated"));
        fetchItems();
      })
      .catch((err) => {
        setSavingId(null);
        toast.error(err);
      });
  }

  function startEdit(item) {
    setEditingId(item.id);
    setDrafts((prev) => ({
      ...prev,
      [item.id]: item.label || "",
    }));
  }

  function cancelEdit(item) {
    setEditingId(null);
    setDrafts((prev) => ({
      ...prev,
      [item.id]: item.label || "",
    }));
  }

  if (loading) {
    return (
      <Group justify="center" py="md">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <Stack gap="sm" mt="sm">
      <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_CREATE}>
        {!showCreate ? (
          <Group>
            <Button size="xs" variant="light" onClick={() => setShowCreate(true)}>
              {t("add_a_choice")}
            </Button>
          </Group>
        ) : (
          <Card withBorder>
            <Group justify="space-between" mb="sm">
              <Title order={5}>
                {t("add_a_choice")}
              </Title>
              <Button
                size="xs"
                variant="subtle"
                onClick={() => {
                  setShowCreate(false);
                  setLabel("");
                  setPosition(null);
                }}
              >
                {t("cancel")}
              </Button>
            </Group>
            <Stack gap="sm">
              <TextInput
                label={t("name")}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
              />
              <NumberInput
                label="Position"
                value={position}
                onChange={setPosition}
                min={1}
              />
              <Button loading={creating} onClick={onCreate}>
                {t("create")}
              </Button>
            </Stack>
          </Card>
        )}
      </Can>

      {!items.length && <EmptyMessage />}
      {items.map((item) => (
        <Card key={item.id} withBorder>
          <Stack gap="sm">
            <Group justify="space-between" align="flex-start">
              <div>
                <Text fw={600}>{item.label}</Text>
                {item.position != null && (
                  <Text size="sm" c="dimmed">
                    Position {item.position}
                  </Text>
                )}
              </div>
              <Group gap="xs">
                <Badge variant="light" color={statusColor(item.status)}>
                  {item.status_formatted || t("status")}
                </Badge>
                <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_EDIT}>
                  <ActionIcon
                    size="sm"
                    variant="light"
                    color={item.status === 1 ? "red" : "green"}
                    title={t("activation_status")}
                    loading={togglingId === item.id}
                    onClick={() => toggleActive(item.id)}
                  >
                    <IconPower size={14} />
                  </ActionIcon>
                  {editingId !== item.id && (
                    <ActionIcon
                      size="sm"
                      variant="light"
                      title={t("edit")}
                      onClick={() => startEdit(item)}
                    >
                      <IconPencil size={14} />
                    </ActionIcon>
                  )}
                </Can>
              </Group>
            </Group>

            {editingId === item.id && (
              <Can permission={PERMISSIONS.CONCEPTUAL_ITEMS_EDIT}>
                <Stack gap="sm">
                  <TextInput
                    label={t("name")}
                    value={drafts[item.id] ?? ""}
                    onChange={(e) =>
                      setDrafts((prev) => ({
                        ...prev,
                        [item.id]: e.target.value,
                      }))
                    }
                  />
                  <Group>
                    <Button
                      loading={savingId === item.id}
                      onClick={() => updateLabel(item.id)}
                    >
                      {t("save")}
                    </Button>
                    <Button
                      variant="default"
                      onClick={() => cancelEdit(item)}
                    >
                      {t("cancel")}
                    </Button>
                  </Group>
                </Stack>
              </Can>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
